// DTO de D3 — Análisis Mensual del directivo.
//
//   - GET /api/directivo/analisis-mensual?mes=YYYY-MM-DD (cualquier día del mes)
//
// Cadencia mensual (review estratégico): KPIs del mes vs anterior, tendencia de
// cumplimiento 6 meses por planta, capacidad utilizada estacional 12 meses.
// SOLO LECTURA. Métricas financieras (margen) diferidas a F2.
import { z } from 'zod';
import { pendienteValidacionUtilidadSchema, sinCostoMoSchema, sinCostoMoTiendaSchema, sinTarifaSchema, sinTarifaTiendaSchema, } from './coordinador_cumplimiento.js';
export const metricaSerieSchema = z.enum(['cumplimiento', 'produccion', 'paros']);
export const directivoAnalisisMensualQuerySchema = z.object({
    /** Cualquier día del mes objetivo. Si se omite, mes en curso. */
    mes: z.string().date().optional(),
});
// =============================================================================
// KPIs (4 cards)
// =============================================================================
export const analisisMensualKpisSchema = z.object({
    cumplimiento_mes: z.object({
        valor_pct: z.number().min(0).nullable(),
        /** Delta en pp vs mes anterior. */
        delta_pp: z.number().nullable(),
        mes_anterior_label: z.string(),
    }),
    produccion_total: z.object({
        cantidad: z.number().min(0),
        /** Meta (programa Alpina) del mes. */
        meta: z.number().min(0),
    }),
    capacidad_utilizada: z.object({
        valor_pct: z.number().min(0).nullable(),
        /** Promedio Q1 del año (ene-mar) para comparativo. */
        promedio_q1_pct: z.number().min(0).nullable(),
    }),
    /**
     * Margen operativo % de LÍNEAS (utilidad/valor). null si no hay costos/precios
     * cargados o si el rol no puede ver costos. Espejo de `rentabilidad.utilidad_pct`.
     */
    margen_operativo_pct: z.number().nullable(),
});
// =============================================================================
// Rentabilidad en pesos ($) — WS-5 B4b + tiendas (unificación 2026-07-03)
// =============================================================================
export const rentabilidadMensualSchema = z.object({
    valor_producido: z.number(),
    costo_mano_obra: z.number(),
    utilidad: z.number(),
    utilidad_pct: z.number().nullable(),
    /** true si el mes NO tiene plata de tiendas (el bloque de abajo llega null). */
    solo_lineas: z.boolean(),
    /** DN9: producción de líneas sin precio vigente — fuera del cálculo, declarada. */
    sin_tarifa: sinTarifaSchema.optional(),
    sin_costo_mo: sinCostoMoSchema.optional(),
    paquetes: z.number().min(0).optional(),
    /** DN10: todo el dinero de este bloque es sobre producción VALIDADA. */
    base_utilidad: z.literal('validada').optional(),
    /** DN10: capturado − validado de líneas — declarado, NO sumado. */
    pendiente_validacion_utilidad: pendienteValidacionUtilidadSchema.nullable().optional(),
});
/** Rentabilidad del reempaque de tiendas — los insumos (sticker/termo) se
 *  COBRAN al cliente: suman al valor. null si el mes no tiene corridas con plata. */
export const rentabilidadTiendasMensualSchema = z.object({
    valor_unidades: z.number(),
    ingreso_insumos: z.number(),
    valor_producido: z.number(),
    costo_mano_obra: z.number(),
    utilidad: z.number(),
    utilidad_pct: z.number().nullable(),
    sin_tarifa: sinTarifaTiendaSchema.optional(),
    sin_costo_mo: sinCostoMoTiendaSchema.optional(),
    /** DN10: base validada, misma que las líneas. */
    base: z.literal('validada').optional(),
    /** DN10: corridas capturadas sin validar — declaradas, NO sumadas. */
    pendiente_validacion: pendienteValidacionUtilidadSchema.nullable().optional(),
    destinos: z.array(z.object({
        tienda_id: z.string().uuid(),
        tienda_codigo: z.string(),
        tienda_nombre: z.string(),
        valor_unidades: z.number(),
        ingreso_insumos: z.number(),
        valor_producido: z.number(),
        costo_mano_obra: z.number(),
        utilidad: z.number(),
        utilidad_pct: z.number().nullable(),
        sin_tarifa: sinTarifaTiendaSchema.optional(),
        sin_costo_mo: sinCostoMoTiendaSchema.optional(),
    })),
});
// =============================================================================
// Tendencia 6 meses por planta (multi-línea)
// =============================================================================
export const mesPuntoSchema = z.object({
    /** YYYY-MM (primer día del mes). */
    mes_iso: z.string(),
    /** Etiqueta corta es-CO ("Dic"). */
    label: z.string(),
});
export const tendenciaPuntoSchema = z.object({
    mes_iso: z.string(),
    planta_id: z.string().uuid(),
    cumplimiento_pct: z.number().nullable(),
    produccion: z.number().min(0),
    paros_horas: z.number().min(0),
});
export const tendenciaSchema = z.object({
    meses: z.array(mesPuntoSchema),
    plantas: z.array(z.object({
        planta_id: z.string().uuid(),
        nombre: z.string(),
        /** Planta foco (resaltada en el gráfico). */
        es_foco: z.boolean(),
    })),
    puntos: z.array(tendenciaPuntoSchema),
});
// =============================================================================
// Estacional 12 meses (capacidad utilizada agregada)
// =============================================================================
export const estacionalSchema = z.object({
    meses: z.array(mesPuntoSchema),
    /** Capacidad utilizada agregada por mes (0-100). null si sin data ese mes. */
    puntos: z.array(z.object({ mes_iso: z.string(), capacidad_pct: z.number().nullable() })),
    promedio_pct: z.number().min(0).nullable(),
    /** Meses YYYY-MM marcados como temporada alta (banda visual). */
    temporada_alta_meses: z.array(z.string()),
});
// =============================================================================
// Response
// =============================================================================
export const directivoAnalisisMensualSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    mes: z.object({
        /** Primer día del mes objetivo (YYYY-MM-DD). */
        desde: z.string().date(),
        /** Etiqueta "Mayo 2026". */
        label: z.string(),
        dias_con_datos: z.number().int().min(0),
        plantas_total: z.number().int().min(0),
    }),
    kpis: analisisMensualKpisSchema,
    /** Capacidad "ver costos/rentabilidad" del rol/usuario. Gate del FE. */
    puede_ver_costos: z.boolean(),
    /** Rentabilidad de líneas ($). null si no puede ver costos. */
    rentabilidad: rentabilidadMensualSchema.nullable(),
    rentabilidad_tiendas: rentabilidadTiendasMensualSchema.nullable(),
    tendencia: tendenciaSchema,
    estacional: estacionalSchema,
    actualizado_at: z.string().datetime(),
});
//# sourceMappingURL=directivo_analisis_mensual.js.map